import { Layer, TableToolbar, TableToolbarContent } from "@carbon/react";
import { NullableTableColumns } from "@flaminem/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { array, number, object, preprocess, record, string, infer as infer_ } from "zod";

import { NumberInputFormField } from "src/components/Form/NumberInputFormField.tsx";
import { InlineNotification } from "src/components/Notifications/InlineNotification.tsx";
import { FixedTable } from "src/components/Tables/FixedTable.tsx";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch.tsx";
import { useGetRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { useT } from "src/internationalization";
import { Rule } from "src/types/rule.ts";
import { DefinedScenarioRule } from "src/types/scenario.ts";

import { RuleWithScore } from "../../pages/Scenarios/Scenarios.types.ts";

import { searchForRule } from "./searchForRule.ts";

export type RulesFormRef = {
  submit: (onSubmit: (rules: RuleWithScore[]) => void, onError: () => void) => () => void;
};

export type RulesFormProps = {
  caseTypeCode?: string;
  title: string;
  subtitle?: string;
  defaultScenarioRules?: DefinedScenarioRule[];
  onValidChange: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

type FormState = {
  selectedRuleIds: string[];
  scores: Record<string, { value: string; isSelected: boolean }>;
};

type ValidatedFormState = infer_<ReturnType<typeof useSchema>>;

export const RulesForm = forwardRef<RulesFormRef, RulesFormProps>(function ScenarioVersionRulesForm(
  { caseTypeCode, onValidChange, onDirtyChange, title, subtitle, defaultScenarioRules = [] },
  ref
) {
  const t = useT();
  const validationSchema = useSchema();
  const [search, setSearch] = useState("");
  const {
    control,
    formState: { errors, isValid, isSubmitted, isDirty },
    handleSubmit,
    setValue,
    trigger,
    reset,
  } = useForm<FormState>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      selectedRuleIds: [],
      scores: {},
    },
  });

  useEffect(() => {
    onValidChange(isSubmitted ? isValid : true);
    // We do not want to fire this effect when onValidChange changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const {
    data: rules,
    loading,
    error,
  } = useGetRulesQuery({
    variables: caseTypeCode ? { caseTypeCode: caseTypeCode } : undefined,
    onCompleted: (data) => {
      if (!data.rules) return;

      const scores = data.rules.reduce((acc, rule) => {
        const scenarioRule = defaultScenarioRules.find(
          (scenarioRule) => scenarioRule.rule.id === rule.id
        );
        return { ...acc, [rule.id]: { value: scenarioRule ? scenarioRule.score.toString() : "0" } };
      }, {});

      reset({
        scores,
        selectedRuleIds: defaultScenarioRules.map((rule) => rule.rule.id),
      });
    },
  });

  const sortedRules = useMemo(() => {
    if (!rules) return [];
    const selectedRules = defaultScenarioRules.map((scenarioRule) => scenarioRule.rule.id);

    return rules.toSorted((a, b) => {
      if (selectedRules.includes(a.id) && selectedRules.includes(b.id)) return 0;
      if (selectedRules.includes(a.id)) return -1;
      if (selectedRules.includes(b.id)) return 1;
      return 0;
    });
  }, [defaultScenarioRules, rules]);

  const columns: NullableTableColumns<Rule> = {
    name: {
      label: t("general.table.name.column.title"),
      render: (rule) => rule.name,
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    code: {
      label: t("general.table.code.column.title"),
      render: (rule) => rule.code,
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    description: {
      label: t("general.table.description.column.title"),
      render: (rule) => rule.description,
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    score: {
      label: t("rules.table.score.column.title"),
      render: (rule, _, state) => {
        return state.isSelected ? (
          <NumberInputFormField
            id="something"
            min={0}
            control={control}
            name={`scores.${rule.id}.value`}
          />
        ) : null;
      },
      width: 50,
      flexBasis: 50,
      flexGrow: 1,
      flexShrink: 2,
      className: "max-w-[350px] !min-w-[200px]",
      headerClassName: "max-w-[350px] !min-w-[200px]",
    },
  };

  useImperativeHandle(ref, () => {
    return {
      submit: (onSubmit, onError) =>
        handleSubmit((data) => {
          const validatedData = data as unknown as ValidatedFormState;

          const rulesWithScore = rules
            .filter((rule) => validatedData.selectedRuleIds.includes(rule.id))
            .map((rule) => ({ ...rule, score: validatedData.scores[rule.id].value }));

          onSubmit(rulesWithScore);
        }, onError),
    };
  });

  const handleSelect = useCallback(
    (newSelectedRules: Rule[]) => {
      setValue(
        "selectedRuleIds",
        newSelectedRules.map((rule) => rule.id)
      );

      if (isSubmitted) trigger();
    },
    [isSubmitted, setValue, trigger]
  );

  return (
    <Layer level={0} className="flex flex-col h-full gap-5">
      {errors.selectedRuleIds && (
        <div className="flex flex-col px-5">
          <InlineNotification
            kind="error"
            title={t("general.error.validation.title")}
            subtitle={errors.selectedRuleIds.message}
            fullWidth
          />
        </div>
      )}
      <FixedTable
        id="scenarioVersionRules"
        data={sortedRules}
        selectedRows={defaultScenarioRules.map((scenarioRule) => scenarioRule.rule)}
        isSelectable={true}
        title={title}
        description={subtitle}
        isLoading={loading}
        pageSize={10}
        isError={!!error}
        columns={columns}
        rowHeight={75}
        toolbar={() => (
          <TableToolbar aria-label="data table toolbar">
            <TableToolbarContent className="border-b border-solid border-borderSubtle01">
              <TableToolbarSearch
                id="searchBlacklistable"
                onSearchChange={setSearch}
                search={search}
              />
            </TableToolbarContent>
          </TableToolbar>
        )}
        onSelect={handleSelect}
        filter={(rule) => searchForRule(search, rule)}
      />
    </Layer>
  );
});

const useSchema = () => {
  const t = useT();

  return useMemo(() => {
    // Schema for scores record values
    const scoreValueSchema = object({
      value: preprocess(
        (val) => {
          // Default undefined values to 0, or convert string to a number
          if (val === undefined) return 0;
          if (typeof val === "string") return parseFloat(val);
          return val;
        },
        number({ message: t("scenarios.form.rules.score.error.required") })
      ),
    });

    return object({
      selectedRuleIds: array(string()).min(1, { message: t("scenarios.form.rules.error.min") }),
      scores: record(scoreValueSchema),
    }).superRefine((data, ctx) => {
      data.selectedRuleIds.forEach((id) => {
        const value = data.scores[id]?.value;

        if (value === undefined) {
          ctx.addIssue({
            code: "invalid_type",
            message: t("scenarios.form.rules.score.error.required"),
            expected: "number",
            received: "undefined",
            path: ["scores", id, "value"],
          });

          return;
        }

        if (value < 1) {
          ctx.addIssue({
            code: "too_small",
            message: t("scenarios.form.rules.score.error.min", { min: 1 }),
            inclusive: true,
            minimum: 1,
            type: "number",
            path: ["scores", id, "value"],
          });
        }
      });
    });
  }, [t]);
};
